import * as THREE from 'three';
import { COURT, GOAL, GOALKEEPER_AREA, PLAYER } from '../game/constants';
import { WEAPONS, ROCKET, isHeavyWeapon } from '../game/weapons';
import type { ControlIntent } from '../core/InputController';
import type { PlayerEntity } from '../entities/PlayerEntity';

export interface AiContext {
  ballPos: THREE.Vector3;
  ballVel: THREE.Vector3;
  ballOwnerId: string | null;
  ballOwnerTeam: 'A' | 'B' | null;
  active: boolean;
  findWeapon?: (position: THREE.Vector3, maxDistance: number) => THREE.Vector3 | null;
}

interface AiMemory {
  targetX: number;
  targetZ: number;
  decisionTimer: number;
  slideTimer: number;
  punchTimer: number;
  reactionTimer: number;
  planTimer: number;
  lane: number;
  aggression: number;
  heldTime: number;
  ownerId: string | null | undefined;
}

const GOAL_ATTACK_Z = GOAL.halfWidth * 0.78;

export class AiSystem {
  private readonly intents = new Map<string, ControlIntent>();
  private readonly memory = new Map<string, AiMemory>();
  private skill = 0.78;

  constructor(private readonly players: PlayerEntity[], private readonly rng: () => number) {
    for (const player of players) {
      this.intents.set(player.id, {moveX: 0, moveZ: 0, sprint: false, shootHeld: false, shootPressed: false, shootReleased: false, passPressed: false, punchPressed: false, active: false});
      this.memory.set(player.id, {
        targetX: player.position.x, targetZ: player.position.z, decisionTimer: 0,
        slideTimer: 0, punchTimer: 0, reactionTimer: 0, planTimer: 0,
        lane: this.rng() < 0.5 ? -1 : 1, aggression: 0.12 + this.rng() * 0.48,
        heldTime: 0, ownerId: undefined,
      });
    }
  }

  setSkill(skill: number): void { this.skill = THREE.MathUtils.clamp(skill, 0.2, 1); }
  getIntent(playerId: string): ControlIntent | null { return this.intents.get(playerId) ?? null; }

  update(delta: number, context: AiContext): void {
    for (const player of this.players) {
      const intent = this.intents.get(player.id);
      const memory = this.memory.get(player.id);
      if (!intent || !memory) continue;
      intent.moveX = intent.moveZ = 0;
      intent.lookX = intent.lookZ = undefined;
      intent.sprint = intent.shootHeld = intent.shootPressed = intent.shootReleased = intent.passPressed = intent.punchPressed = intent.active = false;
      memory.decisionTimer -= delta;
      memory.planTimer -= delta;
      memory.slideTimer = Math.max(0, memory.slideTimer - delta);
      memory.punchTimer = Math.max(0, memory.punchTimer - delta);
      memory.heldTime = context.ballOwnerId === player.id ? memory.heldTime + delta : 0;
      if (memory.ownerId !== context.ballOwnerId) {
        memory.ownerId = context.ballOwnerId;
        memory.decisionTimer = memory.planTimer = 0;
        memory.heldTime = 0;
      }
      if (!context.active || player.controlledBy !== 'ai' || player.eliminated) continue;
      if (!player.canAct && player.state !== 'gkGuard' && player.state !== 'gkDive') continue;
      intent.active = true;
      if (player.isGoalkeeper) this.updateGoalkeeper(player, memory, intent, delta, context);
      else this.updateFieldPlayer(player, memory, intent, context);
    }
  }

  private updateFieldPlayer(player: PlayerEntity, memory: AiMemory, intent: ControlIntent, context: AiContext): void {
    const sign = player.team === 'A' ? 1 : -1;
    const goalX = sign * COURT.halfLength;
    const ownGoalX = -goalX;
    const ball = context.ballPos;
    const iHaveBall = context.ballOwnerId === player.id;
    const weHaveBall = context.ballOwnerTeam === player.team;
    const owner = this.players.find(p => p.id === context.ballOwnerId);
    let chaser = player, bestDistance = Infinity;
    for (const mate of this.players) {
      if (mate.team !== player.team || mate.isGoalkeeper || mate.isDown || mate.eliminated) continue;
      const distance = Math.hypot(mate.position.x - ball.x, mate.position.z - ball.z) / (0.5 + 0.5 * mate.hp / mate.maxHp);
      if (distance < bestDistance) { bestDistance = distance; chaser = mate; }
    }
    const isChaser = chaser === player;
    const opponent = this.nearestOpponent(player);
    const opponentDistance = opponent ? player.position.distanceTo(opponent.position) : Infinity;
    const decide = memory.decisionTimer <= 0;
    if (decide) {
      // Hold tactical choices for a few tenths of a second instead of rolling attacks every frame.
      memory.decisionTimer = 0.24 + (1 - this.skill) * 0.18 + this.rng() * 0.2;
      if (memory.planTimer <= 0) {
        memory.planTimer = 1.8 + this.rng() * 2;
        if (this.rng() < 0.55) memory.lane *= -1;
      }
      if (iHaveBall) {
        const goalDistance = Math.abs(goalX - player.position.x);
        const keeper = this.players.find(p => p.team !== player.team && p.isGoalkeeper && !p.isDown);
        const aimZ = keeper ? -(Math.sign(keeper.position.z) || memory.lane) * GOAL_ATTACK_Z : memory.lane * GOAL_ATTACK_Z;
        const shotClear = this.laneClearance(player, goalX, aimZ, false) > 1;
        const mate = player.weaponType ? null : this.getPassTarget(player);
        const forwardMate = mate ? (mate.position.x - player.position.x) * sign : -Infinity;
        const roll = this.rng();
        if (goalDistance < 11 && Math.abs(player.position.z) < 6.5 && shotClear && opponentDistance > 1.25 &&
            (goalDistance < 5 || memory.heldTime > 2.5 || roll < 0.35 + this.skill * 0.45)) {
          const dx = goalX - player.position.x, dz = aimZ - player.position.z;
          const length = Math.hypot(dx, dz) || 1;
          intent.moveX = intent.lookX = dx / length;
          intent.moveZ = intent.lookZ = dz / length;
          intent.shootPressed = true;
          memory.decisionTimer = 0.65;
        } else if (mate && (opponentDistance < 3 || !shotClear || memory.heldTime > 3 || (forwardMate > 2 && roll < 0.55))) {
          intent.passPressed = true;
          memory.decisionTimer = 0.7;
        }
        memory.targetX = goalX - sign * 2;
        memory.targetZ = memory.lane * (Math.abs(player.position.z) > 4 ? 1.2 : 3.8);
        if (opponent && opponentDistance < 3.4) {
          const away = Math.sign(player.position.z - opponent.position.z) || memory.lane;
          memory.targetX = player.position.x + sign * 2;
          memory.targetZ = player.position.z + away * 3.4;
        }
      } else if (weHaveBall) {
        const advance = memory.aggression > 0.5 || (owner?.position.x ?? ball.x) * sign < -5 ? 4.2 : -1.6;
        const lane = Math.abs(ball.z) > 2 ? -Math.sign(ball.z) : memory.lane;
        memory.targetX = (owner?.position.x ?? ball.x) + sign * advance;
        memory.targetZ = ball.z * 0.35 + lane * (2.5 + memory.aggression * 2);
      } else if (isChaser) {
        const lead = Math.min(0.45, bestDistance / 14);
        memory.targetX = ball.x + context.ballVel.x * lead - (owner ? sign * 0.65 : 0);
        memory.targetZ = ball.z + context.ballVel.z * lead;
      } else {
        const runner = this.players.find(p => p.team !== player.team && !p.isGoalkeeper && p !== owner && !p.isDown && !p.eliminated &&
          p.position.x * sign < ball.x * sign + 2 && Math.abs(p.position.x - ownGoalX) < 12);
        memory.targetX = runner ? runner.position.x - sign * 1.4 : ownGoalX + sign * 4.4 + THREE.MathUtils.clamp((ball.x - ownGoalX) * sign * 0.35, 0, 5) * sign;
        memory.targetZ = runner ? runner.position.z : ball.z * 0.55 + memory.lane * 1.2;
      }
      if (!iHaveBall && (!isChaser || weHaveBall) && !player.weaponType && player.hp > player.maxHp * 0.3 &&
          (weHaveBall || ball.x * sign > -7)) {
        const pickup = context.findWeapon?.(player.position, 4);
        if (pickup) { memory.targetX = pickup.x; memory.targetZ = pickup.z; }
      }
      memory.targetX = THREE.MathUtils.clamp(memory.targetX, -COURT.halfLength + 1, COURT.halfLength - 1);
      memory.targetZ = THREE.MathUtils.clamp(memory.targetZ, -COURT.halfWidth + 1, COURT.halfWidth - 1);
    }
    if (!intent.shootPressed) {
      const dx = memory.targetX - player.position.x, dz = memory.targetZ - player.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance > 0.3) { intent.moveX = dx / distance; intent.moveZ = dz / distance; }
      intent.sprint = distance > 5 && player.stamina > 40 && player.hp > player.maxHp * 0.35;
    }
    if (!opponent || intent.shootPressed || intent.passPressed) return;
    const dx = opponent.position.x - player.position.x, dz = opponent.position.z - player.position.z;
    const distance = Math.hypot(dx, dz) || 0.001;
    if (player.weaponType) {
      const weapon = player.weaponType;
      if (distance < WEAPONS[weapon].range + 0.3) {
        intent.lookX = dx / distance; intent.lookZ = dz / distance;
      }
      const unsafeRocket = weapon === 'bazooka' && (distance < ROCKET.radius + 1 || this.players.some(p =>
        p !== player && p.team === player.team && !p.eliminated && p.hp > 0 && p.position.distanceTo(opponent.position) < ROCKET.radius + 0.5));
      if (unsafeRocket && !iHaveBall && distance < ROCKET.radius + 1) { intent.moveX = -dx / distance; intent.moveZ = -dz / distance; }
      if (decide && memory.punchTimer <= 0 && player.punchCooldown <= 0 && !unsafeRocket) {
        const facing = (dx * player.facing.x + dz * player.facing.z) / distance;
        if (distance < WEAPONS[weapon].range && facing > 0.5 && this.rng() < 0.65 + memory.aggression * 0.25) {
          intent.punchPressed = true;
          memory.punchTimer = (isHeavyWeapon(weapon) ? 2.3 : 1.1) + this.rng() * 0.7;
        } else if (!iHaveBall && weapon !== 'bazooka' && weapon !== 'shotgun' && distance > 3 && distance < 7 && this.rng() < 0.25) {
          intent.passPressed = true; memory.punchTimer = 2;
        }
        if (intent.punchPressed || intent.passPressed) { intent.moveX = dx / distance; intent.moveZ = dz / distance; }
      }
    } else if (decide && memory.punchTimer <= 0 && player.punchCooldown <= 0 && distance < PLAYER.punchRange + 0.25 &&
        (opponent.id === context.ballOwnerId || iHaveBall || this.rng() < memory.aggression)) {
      const roll = this.rng();
      if (!iHaveBall && memory.slideTimer <= 0 && player.slideCooldown <= 0 && distance > 1.05 && roll < 0.45) {
        intent.shootHeld = true; memory.slideTimer = 2.5 + this.rng();
      } else if (roll < 0.85) {
        intent.punchPressed = true; memory.punchTimer = 1 + this.rng() * 0.9;
      }
      if (intent.punchPressed || intent.shootHeld) { intent.moveX = dx / distance; intent.moveZ = dz / distance; }
    }
  }

  private nearestOpponent(player: PlayerEntity): PlayerEntity | null {
    let nearest: PlayerEntity | null = null, best = Infinity;
    for (const other of this.players) {
      if (other.team === player.team || other.isDown || other.eliminated) continue;
      const distance = player.position.distanceToSquared(other.position);
      if (distance < best) { best = distance; nearest = other; }
    }
    return nearest;
  }

  private laneClearance(player: PlayerEntity, x: number, z: number, includeKeeper = true): number {
    const dx = x - player.position.x, dz = z - player.position.z;
    const lengthSq = dx * dx + dz * dz;
    if (lengthSq < 0.01) return 0;
    let clearance = 8;
    for (const other of this.players) {
      if (other.team === player.team || other.isDown || other.eliminated || (!includeKeeper && other.isGoalkeeper)) continue;
      const t = ((other.position.x - player.position.x) * dx + (other.position.z - player.position.z) * dz) / lengthSq;
      if (t < 0.04 || t > 0.98) continue;
      clearance = Math.min(clearance, Math.hypot(other.position.x - player.position.x - dx * t, other.position.z - player.position.z - dz * t));
    }
    return clearance;
  }

  getPassTarget(player: PlayerEntity): PlayerEntity | null {
    let best: PlayerEntity | null = null, bestScore = -Infinity;
    const sign = player.team === 'A' ? 1 : -1;
    for (const mate of this.players) {
      if (mate.team !== player.team || mate === player || !mate.canAct || mate.eliminated) continue;
      const distance = player.position.distanceTo(mate.position);
      if (distance < 2 || distance > 14 || this.laneClearance(player, mate.position.x, mate.position.z) < 1.15) continue;
      let openness = 8;
      for (const other of this.players) {
        if (other.team !== player.team && !other.isDown && !other.eliminated) openness = Math.min(openness, other.position.distanceTo(mate.position));
      }
      if (openness < 1.3) continue;
      const score = openness + (mate.position.x - player.position.x) * sign * 0.55 - distance * 0.12 - (mate.isGoalkeeper ? 4 : 0);
      if (score > bestScore) { bestScore = score; best = mate; }
    }
    return best;
  }

  private updateGoalkeeper(
    player: PlayerEntity,
    memory: AiMemory,
    intent: ControlIntent,
    delta: number,
    context: AiContext,
  ): void {
    if (player.state === 'gkCatch' || player.state === 'gkThrow' || player.state === 'gkDive') return;
    const side = player.team === 'A' ? -1 : 1;
    const goalX = side * COURT.halfLength;
    const ball = context.ballPos;
    const lookX = ball.x - player.position.x;
    const lookZ = ball.z - player.position.z;
    const lookLength = Math.hypot(lookX, lookZ);
    intent.lookX = lookLength > 0.1 ? lookX / lookLength : -side;
    intent.lookZ = lookLength > 0.1 ? lookZ / lookLength : 0;
    const inArea = Math.abs(goalX - player.position.x) <= GOALKEEPER_AREA.depth
      && Math.abs(player.position.z) <= GOALKEEPER_AREA.halfWidth;

    const goalDistance = (goalX - ball.x) * side;
    const towardGoalSpeed = context.ballVel.x * side;
    let shadowing = false;

    if (inArea && towardGoalSpeed > 5 && goalDistance > 0.4 && goalDistance < 17) {
      const time = goalDistance / towardGoalSpeed;
      if (time > 0 && time < 0.95) {
        const interceptZ = ball.z + context.ballVel.z * time;
        if (Math.abs(interceptZ) < GOAL.halfWidth + 1.25) {
          shadowing = true;
          memory.reactionTimer += delta;
          const reaction = 0.45 - this.skill * 0.27;
          if (memory.reactionTimer > reaction && (player.state === 'gkGuard' || player.state === 'run')) {
            const diveTarget = new THREE.Vector3(goalX - side * 0.7, 0, interceptZ);
            const diveDir = new THREE.Vector3().subVectors(diveTarget, player.position);
            if (diveDir.length() > 0.25) {
              player.startDive(diveDir.normalize());
              memory.reactionTimer = 0;
            }
          }
        }
      }
    }
    if (!shadowing) {
      memory.reactionTimer = Math.max(0, memory.reactionTimer - delta * 2.5);
    }

    const guardZ = inArea
      ? THREE.MathUtils.clamp(ball.z * 0.72, -GOAL.halfWidth - 0.45, GOAL.halfWidth + 0.45)
      : 0;
    const loose = context.ballOwnerId === null;
    const inOwnHalf = ball.x * side > 0;
    const closeBall = ball.distanceTo(player.position) < 5.5;
    const rush = inArea && loose && inOwnHalf && closeBall ? 0.85 : 0;
    const guardX = goalX - side * (0.7 + rush);

    if (player.state === 'gkGuard' || player.state === 'run' || player.state === 'idle') {
      const dx = guardX - player.position.x;
      const dz = guardZ - player.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance > 0.18) {
        intent.moveX = dx / distance;
        intent.moveZ = dz / distance;
      }
      intent.sprint = Math.abs(dz) > 3.4;
    }

    if (context.ballOwnerId === player.id) return;

    const carrier = this.players.find((p) => p.id === context.ballOwnerId);
    if (carrier && carrier.team !== player.team && !carrier.isGoalkeeper) {
      const carrierDistance = carrier.position.distanceTo(player.position);
      const reach = rush > 0 ? 1.6 : 1.15;
      if (carrierDistance < reach && player.state === 'gkGuard' && loose === false) {
        if (this.rng() < 0.6 + this.skill * 0.3) {
          intent.punchPressed = true;
        }
      }
    }
  }
}
